import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserRole, type RobotSummaryDto } from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, FormError, Textarea, Field, Input } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { toYmdInput } from '@/lib/format-date';
import { RobotForm, valuesToUpdateBody, type RobotFormValues } from './RobotForm';

interface Props {
  open: boolean;
  onClose: () => void;
  robot: RobotSummaryDto;
}

export function RobotEditModal({ open, onClose, robot }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  // 회계 필드는 ADMIN 전용(@DeniedForRole). 비-ADMIN 이 PATCH 본문에 담으면
  // 미변경이라도 403 → valuesToUpdateBody 에 권한을 넘겨 ADMIN 일 때만 포함한다.
  const isAdmin = useAuthStore((s) => s.user?.role) === UserRole.ADMIN;
  const [values, setValues] = useState<RobotFormValues>(() => dtoToForm(robot));
  const [error, setError] = useState<string | null>(null);
  const [snConfirmOpen, setSnConfirmOpen] = useState(false);
  // 수정 사항2.pdf p.5 #1 — 소유 법인 변경 흐름.
  //   사용자가 ownerEntity 를 다른 값으로 바꾸고 저장하면 transferOpen=true
  //   로 reason 입력 dialog 가 뜬다. reason 확정 후 transfer() → update()
  //   순서로 호출하고, 한쪽이라도 실패하면 toast 로 알린다.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferReason, setTransferReason] = useState('');
  const [transferDocRef, setTransferDocRef] = useState('');

  useEffect(() => {
    if (open) {
      setValues(dtoToForm(robot));
      setError(null);
      setTransferReason('');
      setTransferDocRef('');
    }
  }, [open, robot]);

  const update = useMutation({
    mutationFn: () =>
      robotsApi.update(robot.id, valuesToUpdateBody(values, { canEditAccounting: isAdmin })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      toast.success('로봇 정보를 저장했습니다.', '수정 완료');
      setSnConfirmOpen(false);
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  // 수정 사항2.pdf p.5 #1 — 소유 법인 변경 mutation.
  // 성공 시 추가 필드가 바뀌어 있으면 update() 를 이어서 호출.
  const transfer = useMutation({
    mutationFn: () =>
      robotsApi.transfer(robot.id, {
        toEntity: values.ownerEntity,
        reason: transferReason.trim(),
        docReference: transferDocRef.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      toast.success('소유 법인을 이관했습니다. 이관 기록이 남았습니다.', '이관 완료');
      setTransferOpen(false);
      // 나머지 편집 필드도 동시 저장.
      update.mutate();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="로봇 편집"
      width={560}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="robot-edit-form" disabled={update.isPending}>
            {update.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <FormError>{error}</FormError>
      <RobotForm
        formId="robot-edit-form"
        mode="edit"
        values={values}
        // round11 요구2 — 상태 select 를 실제 전이 가능한 값으로 좁히는 기준.
        // 서버가 가드에 쓰는 값과 동일해야 하므로 편집 전 원본(robot prop)을 넘긴다.
        origin={{ status: robot.status, verificationStatus: robot.verificationStatus }}
        onChange={setValues}
        onSubmit={() => {
          setError(null);
          // 수정 사항2.pdf p.5 #1 — 소유 법인 변경은 별도 transfer 엔드포인트.
          // SN 변경 dialog 보다 우선 처리 (감사 로그가 데이터 정합 영향이 더 큼).
          if (values.ownerEntity !== robot.ownerEntity) {
            setTransferOpen(true);
            return;
          }
          // §D-1 #2 (PDF p.8) — SN editable. Warn once if either of
          // the two serials is being changed (compared to the row
          // we opened the modal with). The server-side @unique
          // constraint still rejects collisions; the warning
          // surfaces the impact (RobotQR / Deployment / ImportBatch
          // FK references will all keep pointing at this row by
          // primary key, but on-screen labels and CSV exports start
          // showing the new value).
          const snChanged =
            (values.serialShort ?? '') !== (robot.serialShort ?? '') ||
            (values.serialLong ?? '') !== (robot.serialLong ?? '');
          if (snChanged) {
            setSnConfirmOpen(true);
            return;
          }
          update.mutate();
        }}
      />
      <ConfirmDialog
        open={snConfirmOpen}
        title="시리얼 번호 변경 확인"
        message={[
          `Short SN: ${robot.serialShort ?? '(없음)'} → ${values.serialShort || '(없음)'}`,
          `Long SN:  ${robot.serialLong ?? '(없음)'} → ${values.serialLong || '(없음)'}`,
          '',
          '• QR, 출고 이력, 가져오기 기록과의 연결은 그대로 유지됩니다.',
          '• 화면, CSV, 검색 결과에는 바로 새 값이 보입니다.',
          '• 같은 시리얼이 이미 있으면 저장되지 않습니다.',
          '',
          '계속하시겠습니까?',
        ].join('\n')}
        confirmLabel="변경 확인"
        busy={update.isPending}
        onConfirm={() => update.mutate()}
        onCancel={() => setSnConfirmOpen(false)}
      />

      {/*
       * 수정 사항2.pdf p.5 #1 — 소유 법인 이관 dialog.
       * AssetTransferEvent ledger 가 reason 을 필수로 받기 때문에
       * 단순 ConfirmDialog 대신 입력 form 을 가진 sub-Modal 사용.
       */}
      <Modal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="소유 법인 이관"
        width={460}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setTransferOpen(false)}
              disabled={transfer.isPending}
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={() => transfer.mutate()}
              disabled={transfer.isPending || transferReason.trim().length < 3}
            >
              {transfer.isPending ? '이관 중…' : '이관 실행'}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 12 }}>
          {robot.ownerEntity} → <strong>{values.ownerEntity}</strong>
          <br />
          이관하면 바로 기록이 남고 되돌릴 수 없습니다.
        </div>
        <Field
          label="사유"
          htmlFor="transfer-reason"
          required
          hint="3자 이상 적어 주세요. 나중에 이관 이유를 확인할 수 있는 유일한 기록입니다."
        >
          <Textarea
            id="transfer-reason"
            value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
            rows={3}
            placeholder="예: 법인 분리에 따른 자산 이전, MOU 2026-Q2"
          />
        </Field>
        <Field
          label="문서 참조 (선택)"
          htmlFor="transfer-doc"
          hint="MOU 번호, 티켓 ID, 엑셀 행 번호 등"
        >
          <Input
            id="transfer-doc"
            value={transferDocRef}
            onChange={(e) => setTransferDocRef(e.target.value)}
            placeholder="예: MOU-2026-014"
          />
        </Field>
      </Modal>
    </Modal>
  );
}

function dtoToForm(r: RobotSummaryDto): RobotFormValues {
  return {
    serialShort: r.serialShort ?? undefined,
    serialLong: r.serialLong ?? undefined,
    modelCode: r.model.code,
    status: r.status,
    locationCategory: r.locationCategory,
    verificationStatus: r.verificationStatus,
    accountingBookNo: r.accountingBookNo ?? undefined,
    accountingScheme: r.accountingScheme,
    ownerEntity: r.ownerEntity,
    // §D-1 (PDF p.8) — operator-editable trace fields. Server returns
    // ISO timestamps for the two date columns; <input type="date">
    // expects YYYY-MM-DD, so trim everything after the day.
    color: (r as RobotSummaryDto & { color?: string | null }).color ?? undefined,
    // FB2 §5C — loaderType 제거(구분 사양과 중복). 폼 prefill 미수행.
    // FB §2 — 구분 사양(모델 축 값) 편집 prefill. RobotSummaryDto 에 이미 포함.
    variantValue:
      (r as RobotSummaryDto & { variantValue?: string | null }).variantValue ?? undefined,
    // docs/work_log v1209 — Date 객체 leak 방지를 위해 toYmdInput 사용.
    // toYmdInput 은 빈 문자열 fallback → 빈 문자열이면 undefined 로 변환.
    manufactureCompletedAt:
      toYmdInput(
        (r as RobotSummaryDto & { manufactureCompletedAt?: string | Date | null })
          .manufactureCompletedAt,
      ) || undefined,
    binaryUpdatedAt:
      toYmdInput(
        (r as RobotSummaryDto & { binaryUpdatedAt?: string | Date | null }).binaryUpdatedAt,
      ) || undefined,
    // 초기 데이터 보정 — 첫 제조월/출고월(YYMM) prefill. RobotSummaryDto 에 포함.
    firstProductionYm: r.firstProductionYm ?? undefined,
    firstShipmentYm: r.firstShipmentYm ?? undefined,
    notes: r.notes ?? undefined,
  };
}
